/**
 * Gmail API Integration
 *
 * This module provides Gmail API integration for sending emails.
 *
 * Setup Instructions:
 * 1. Go to Google Cloud Console: https://console.cloud.google.com
 * 2. Create a new project or select existing one
 * 3. Enable the Gmail API
 * 4. Create OAuth 2.0 credentials (Web application type)
 * 5. Add authorized redirect URIs (e.g., http://localhost:3000/auth/google/callback)
 * 6. Add to environment variables:
 *    - REACT_APP_GOOGLE_CLIENT_ID (public - used for OAuth redirect)
 *    - GOOGLE_CLIENT_SECRET (server-side only - Vercel env var)
 *
 * Required OAuth Scopes:
 * - https://www.googleapis.com/auth/gmail.send
 * - https://www.googleapis.com/auth/gmail.compose
 */

import { getConfig } from 'src/api/runtime-config';
import { getAuthHeaders } from 'src/utils/auth-headers';

const GMAIL_API_BASE = 'https://gmail.googleapis.com/gmail/v1';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';

// Storage keys
const ACCESS_TOKEN_KEY = 'gmail_access_token';
const REFRESH_TOKEN_KEY = 'gmail_refresh_token';
const TOKEN_EXPIRY_KEY = 'gmail_token_expiry';

// Email template storage
const EMAIL_TEMPLATES_KEY = 'gmail_email_templates';

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string; // HTML content
  createdAt: string;
  updatedAt: string;
}

export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailAttachment {
  filename: string;
  content: string; // Base64 encoded content
  mimeType: string;
}

export interface EmailMessage {
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  bcc?: EmailRecipient[];
  subject: string;
  body: string; // HTML content
  attachments?: EmailAttachment[];
}

export interface SendEmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
  recipientEmail?: string;
}

export interface BulkSendResult {
  total: number;
  successful: number;
  failed: number;
  results: SendEmailResult[];
}

// Check if Gmail API is configured
export const isGmailConfigured = (): boolean => {
  return Boolean(getConfig('REACT_APP_GOOGLE_CLIENT_ID'));
};

// Check if user is authenticated with Gmail
export const isGmailAuthenticated = (): boolean => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!accessToken || !expiry) return false;

  // Check if token is expired
  const expiryTime = parseInt(expiry, 10);
  return Date.now() < expiryTime;
};

// Get the current access token
export const getAccessToken = (): string | null => {
  if (!isGmailAuthenticated()) return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

// Generate OAuth URL for Gmail authorization
export const getGmailAuthUrl = (): string => {
  const clientId = getConfig('REACT_APP_GOOGLE_CLIENT_ID');
  const redirectUri = `${window.location.origin}/auth/google/callback`;

  const params = new URLSearchParams({
    client_id: clientId || '',
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/userinfo.email',
    access_type: 'offline',
    prompt: 'consent',
  });

  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
};

// Handle OAuth callback and exchange code for tokens
export const handleGmailCallback = async (code: string): Promise<{ success: boolean; error?: string }> => {
  const redirectUri = `${window.location.origin}/auth/google/callback`;

  try {
    // Use a proxy endpoint or serverless function to exchange code for tokens
    const response = await fetch('/api/gmail/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ code, redirectUri }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Gmail token exchange failed:', data);
      return {
        success: false,
        error: data.error || `Token exchange failed (${response.status})`
      };
    }

    // Store tokens
    localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    }
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      String(Date.now() + data.expires_in * 1000)
    );

    return { success: true };
  } catch (error: any) {
    console.error('Gmail OAuth error:', error);
    return { success: false, error: error?.message || 'Network error during token exchange' };
  }
};

// Disconnect Gmail
export const disconnectGmail = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

// Strip newlines/carriage returns to prevent email header injection
const sanitizeHeaderValue = (val: string): string => val.replace(/[\r\n]/g, '');

// Convert email to RFC 2822 format with base64url encoding
const createRawEmail = (message: EmailMessage, fromEmail: string, fromName?: string): string => {
  const hasAttachments = message.attachments && message.attachments.length > 0;
  const boundary = `boundary_${Date.now()}`;
  const altBoundary = `alt_boundary_${Date.now()}`;
  const toHeader = sanitizeHeaderValue(message.to.map(r => r.name ? `"${r.name}" <${r.email}>` : r.email).join(', '));
  const ccHeader = sanitizeHeaderValue(message.cc?.map(r => r.name ? `"${r.name}" <${r.email}>` : r.email).join(', ') || '');
  const bccHeader = sanitizeHeaderValue(message.bcc?.map(r => r.name ? `"${r.name}" <${r.email}>` : r.email).join(', ') || '');

  // Format From header with name if available (sanitized to prevent header injection)
  const fromHeader = sanitizeHeaderValue(fromName ? `"${fromName}" <${fromEmail}>` : fromEmail);

  // Generate unique Message-ID for better deliverability
  const messageId = `<${Date.now()}.${Math.random().toString(36).substring(2)}@relightexteriorlighting.com>`;

  let emailContent = [
    `From: ${fromHeader}`,
    `To: ${toHeader}`,
  ];

  if (ccHeader) emailContent.push(`Cc: ${ccHeader}`);
  if (bccHeader) emailContent.push(`Bcc: ${bccHeader}`);

  emailContent.push(`Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(message.subject)))}?=`);
  emailContent.push(`Reply-To: ${fromHeader}`);
  emailContent.push(`Message-ID: ${messageId}`);
  emailContent.push('X-Mailer: Relight CRM');
  emailContent.push('MIME-Version: 1.0');

  if (hasAttachments) {
    // Use multipart/mixed for attachments, with multipart/alternative for body
    emailContent.push(`Content-Type: multipart/mixed; boundary="${boundary}"`);
    emailContent.push('');
    emailContent.push(`--${boundary}`);
    emailContent.push(`Content-Type: multipart/alternative; boundary="${altBoundary}"`);
    emailContent.push('');
    emailContent.push(`--${altBoundary}`);
    emailContent.push('Content-Type: text/plain; charset="UTF-8"');
    emailContent.push('Content-Transfer-Encoding: 7bit');
    emailContent.push('');
    emailContent.push(message.body.replace(/<[^>]*>/g, '')); // Plain text version
    emailContent.push('');
    emailContent.push(`--${altBoundary}`);
    emailContent.push('Content-Type: text/html; charset="UTF-8"');
    emailContent.push('Content-Transfer-Encoding: 7bit');
    emailContent.push('');
    emailContent.push(message.body);
    emailContent.push('');
    emailContent.push(`--${altBoundary}--`);

    // Add attachments
    for (const attachment of message.attachments!) {
      emailContent.push('');
      emailContent.push(`--${boundary}`);
      emailContent.push(`Content-Type: ${attachment.mimeType}; name="${attachment.filename}"`);
      emailContent.push('Content-Transfer-Encoding: base64');
      emailContent.push(`Content-Disposition: attachment; filename="${attachment.filename}"`);
      emailContent.push('');
      // Split base64 content into lines of 76 characters
      const base64Lines = attachment.content.match(/.{1,76}/g) || [];
      emailContent.push(...base64Lines);
    }

    emailContent.push('');
    emailContent.push(`--${boundary}--`);
  } else {
    // No attachments - use simple multipart/alternative
    emailContent.push(`Content-Type: multipart/alternative; boundary="${boundary}"`);
    emailContent.push('');
    emailContent.push(`--${boundary}`);
    emailContent.push('Content-Type: text/plain; charset="UTF-8"');
    emailContent.push('Content-Transfer-Encoding: 7bit');
    emailContent.push('');
    emailContent.push(message.body.replace(/<[^>]*>/g, '')); // Plain text version
    emailContent.push('');
    emailContent.push(`--${boundary}`);
    emailContent.push('Content-Type: text/html; charset="UTF-8"');
    emailContent.push('Content-Transfer-Encoding: 7bit');
    emailContent.push('');
    emailContent.push(message.body);
    emailContent.push('');
    emailContent.push(`--${boundary}--`);
  }

  const raw = emailContent.join('\r\n');

  // Base64url encode (URL-safe base64)
  return btoa(unescape(encodeURIComponent(raw)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// User profile info
export interface GmailUserProfile {
  email: string;
  name?: string;
  picture?: string;
}

// Get user's profile (email, name, picture)
export const getGmailUserProfile = async (): Promise<GmailUserProfile | null> => {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  try {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v2/userinfo',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    return {
      email: data.email,
      name: data.name,
      picture: data.picture,
    };
  } catch (error) {
    // console.error('Error fetching user profile:', error);
    return null;
  }
};

// Get user's email address (backwards compatible)
export const getGmailUserEmail = async (): Promise<string | null> => {
  const profile = await getGmailUserProfile();
  return profile?.email || null;
};

// Send a single email
export const sendEmail = async (message: EmailMessage): Promise<SendEmailResult> => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return {
      success: false,
      error: 'Not authenticated with Gmail. Please connect your account.',
    };
  }

  try {
    const userProfile = await getGmailUserProfile();
    if (!userProfile?.email) {
      return {
        success: false,
        error: 'Could not determine sender email address.',
      };
    }

    const raw = createRawEmail(message, userProfile.email, userProfile.name);

    const response = await fetch(`${GMAIL_API_BASE}/users/me/messages/send`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ raw }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to send email');
    }

    const data = await response.json();

    return {
      success: true,
      messageId: data.id,
      recipientEmail: message.to[0]?.email,
    };
  } catch (error: any) {
    // console.error('Error sending email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send email',
      recipientEmail: message.to[0]?.email,
    };
  }
};

// Send bulk emails with delay to avoid rate limiting
export const sendBulkEmails = async (
  messages: EmailMessage[],
  onProgress?: (current: number, total: number, result: SendEmailResult) => void,
  delayMs: number = 1000
): Promise<BulkSendResult> => {
  const results: SendEmailResult[] = [];
  let successful = 0;
  let failed = 0;

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const result = await sendEmail(message);

    results.push(result);

    if (result.success) {
      successful++;
    } else {
      failed++;
    }

    if (onProgress) {
      onProgress(i + 1, messages.length, result);
    }

    // Add delay between emails to avoid rate limiting (except for last email)
    if (i < messages.length - 1 && delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return {
    total: messages.length,
    successful,
    failed,
    results,
  };
};

// ============================================
// Email Templates
// ============================================

// Get all saved email templates
export const getEmailTemplates = (): EmailTemplate[] => {
  try {
    const stored = localStorage.getItem(EMAIL_TEMPLATES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save a new email template
export const saveEmailTemplate = (
  name: string,
  subject: string,
  body: string
): EmailTemplate => {
  const templates = getEmailTemplates();

  const newTemplate: EmailTemplate = {
    id: `template_${Date.now()}`,
    name,
    subject,
    body,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  templates.push(newTemplate);
  localStorage.setItem(EMAIL_TEMPLATES_KEY, JSON.stringify(templates));

  return newTemplate;
};

// Update an existing template
export const updateEmailTemplate = (
  id: string,
  updates: Partial<Pick<EmailTemplate, 'name' | 'subject' | 'body'>>
): EmailTemplate | null => {
  const templates = getEmailTemplates();
  const index = templates.findIndex(t => t.id === id);

  if (index === -1) return null;

  templates[index] = {
    ...templates[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(EMAIL_TEMPLATES_KEY, JSON.stringify(templates));

  return templates[index];
};

// Delete a template
export const deleteEmailTemplate = (id: string): boolean => {
  const templates = getEmailTemplates();
  const filtered = templates.filter(t => t.id !== id);

  if (filtered.length === templates.length) return false;

  localStorage.setItem(EMAIL_TEMPLATES_KEY, JSON.stringify(filtered));
  return true;
};

// Get a single template by ID
export const getEmailTemplateById = (id: string): EmailTemplate | null => {
  const templates = getEmailTemplates();
  return templates.find(t => t.id === id) || null;
};

// ============================================
// Email Drafts
// ============================================

const EMAIL_DRAFTS_KEY = 'gmail_email_drafts';

// Customer data stored with draft
export interface DraftCustomerData {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  linearFootage?: number;
  estimatedValue?: number;
}

// Email draft with full context
export interface EmailDraft {
  id: string;
  name: string;
  subject: string;
  body: string;
  // Customer data
  customer?: DraftCustomerData;
  recipients?: EmailRecipient[];
  // Template settings
  designTemplateId?: string;
  builtInTemplateId?: string;
  // Attachments info (not the actual content)
  hasQuoteAttachment?: boolean;
  quoteData?: {
    quoteNumber: string;
    totalAmount: number;
  };
  // Metadata
  createdAt: string;
  updatedAt: string;
}

// Get all saved email drafts
export const getEmailDrafts = (): EmailDraft[] => {
  try {
    const stored = localStorage.getItem(EMAIL_DRAFTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save a new email draft
export const saveEmailDraft = (draft: Omit<EmailDraft, 'id' | 'createdAt' | 'updatedAt'>): EmailDraft => {
  const drafts = getEmailDrafts();

  const newDraft: EmailDraft = {
    ...draft,
    id: `draft_${Date.now()}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  drafts.unshift(newDraft);
  localStorage.setItem(EMAIL_DRAFTS_KEY, JSON.stringify(drafts));

  return newDraft;
};

// Update an existing draft
export const updateEmailDraft = (
  id: string,
  updates: Partial<Omit<EmailDraft, 'id' | 'createdAt'>>
): EmailDraft | null => {
  const drafts = getEmailDrafts();
  const index = drafts.findIndex(d => d.id === id);

  if (index === -1) return null;

  drafts[index] = {
    ...drafts[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(EMAIL_DRAFTS_KEY, JSON.stringify(drafts));
  return drafts[index];
};

// Delete a draft
export const deleteEmailDraft = (id: string): boolean => {
  const drafts = getEmailDrafts();
  const filtered = drafts.filter(d => d.id !== id);

  if (filtered.length === drafts.length) return false;

  localStorage.setItem(EMAIL_DRAFTS_KEY, JSON.stringify(filtered));
  return true;
};

// Get a single draft by ID
export const getEmailDraftById = (id: string): EmailDraft | null => {
  const drafts = getEmailDrafts();
  return drafts.find(d => d.id === id) || null;
};

// ============================================
// Template Variables
// ============================================

export interface TemplateVariables {
  customerName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  projectType?: string;
  linearFootage?: string;
  estimatedValue?: string;
  [key: string]: string | undefined;
}

// HTML-escape a string to prevent XSS
const escapeHtml = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Replace template variables in content
export const replaceTemplateVariables = (
  content: string,
  variables: TemplateVariables
): string => {
  let result = content;

  // Replace {{variableName}} patterns (values are HTML-escaped to prevent injection)
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi');
    result = result.replace(regex, escapeHtml(value || ''));
  });

  return result;
};

// Get available template variables
export const getAvailableVariables = (): { key: string; description: string }[] => {
  return [
    { key: 'customerName', description: 'Full customer name' },
    { key: 'firstName', description: 'Customer first name' },
    { key: 'lastName', description: 'Customer last name' },
    { key: 'email', description: 'Customer email address' },
    { key: 'phone', description: 'Customer phone number' },
    { key: 'address', description: 'Customer street address' },
    { key: 'city', description: 'Customer city' },
    { key: 'state', description: 'Customer state' },
    { key: 'projectType', description: 'Type of project' },
    { key: 'linearFootage', description: 'Total linear footage' },
    { key: 'estimatedValue', description: 'Estimated project value' },
  ];
};

// ============================================
// Inbox / Message Reading
// ============================================

// Attachment info for received emails
export interface GmailAttachmentInfo {
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
}

export interface GmailMessage {
  id: string;
  threadId: string;
  snippet: string;
  from: string;
  fromName: string;
  to: string;
  subject: string;
  date: string;
  timestamp: number;
  isUnread: boolean;
  isStarred: boolean;
  hasReply?: boolean;
  isViewed?: boolean;
  snoozeUntil?: string;
  isActiveConvo?: boolean;
  labelIds: string[];
  body?: string;
  bodyHtml?: string;
  attachments?: GmailAttachmentInfo[];
}

export interface GmailMessageList {
  messages: GmailMessage[];
  nextPageToken?: string;
  resultSizeEstimate: number;
}

// Parse email header value
const parseHeader = (headers: any[], name: string): string => {
  const header = headers?.find((h: any) => h.name.toLowerCase() === name.toLowerCase());
  return header?.value || '';
};

// Parse email address from header (e.g., "John Doe <john@example.com>")
const parseEmailAddress = (headerValue: string): { name: string; email: string } => {
  const match = headerValue.match(/^(?:"?([^"]*)"?\s)?<?([^>]+@[^>]+)>?$/);
  if (match) {
    return {
      name: match[1]?.trim() || match[2],
      email: match[2]
    };
  }
  return { name: headerValue, email: headerValue };
};

// Decode base64url encoded content
const decodeBase64Url = (data: string): string => {
  try {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(escape(atob(base64)));
  } catch {
    return '';
  }
};

// Extract body and attachments from message parts
const extractBodyAndAttachments = (payload: any): { text: string; html: string; attachments: GmailAttachmentInfo[] } => {
  let text = '';
  let html = '';
  const attachments: GmailAttachmentInfo[] = [];

  const processpart = (part: any) => {
    // Check if this part is an attachment
    if (part.filename && part.filename.length > 0 && part.body?.attachmentId) {
      attachments.push({
        attachmentId: part.body.attachmentId,
        filename: part.filename,
        mimeType: part.mimeType || 'application/octet-stream',
        size: part.body.size || 0,
      });
      return;
    }

    // Process body content
    if (part.body?.data) {
      const decoded = decodeBase64Url(part.body.data);
      if (part.mimeType === 'text/html') {
        html = decoded;
      } else if (part.mimeType === 'text/plain') {
        text = decoded;
      }
    }

    // Recursively process nested parts
    if (part.parts) {
      for (const nestedPart of part.parts) {
        processpart(nestedPart);
      }
    }
  };

  // Process the main payload
  if (payload.body?.data) {
    const decoded = decodeBase64Url(payload.body.data);
    if (payload.mimeType === 'text/html') {
      html = decoded;
    } else if (payload.mimeType === 'text/plain') {
      text = decoded;
    }
  }

  // Check main payload for attachment
  if (payload.filename && payload.filename.length > 0 && payload.body?.attachmentId) {
    attachments.push({
      attachmentId: payload.body.attachmentId,
      filename: payload.filename,
      mimeType: payload.mimeType || 'application/octet-stream',
      size: payload.body.size || 0,
    });
  }

  if (payload.parts) {
    for (const part of payload.parts) {
      processpart(part);
    }
  }

  return { text, html, attachments };
};


// List messages from inbox
export const listInboxMessages = async (
  maxResults: number = 20,
  pageToken?: string,
  query?: string
): Promise<GmailMessageList | null> => {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  try {
    const params = new URLSearchParams({
      maxResults: String(maxResults),
      labelIds: 'INBOX',
    });

    if (pageToken) params.append('pageToken', pageToken);
    if (query) params.append('q', query);

    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (!data.messages || data.messages.length === 0) {
      return { messages: [], resultSizeEstimate: 0 };
    }

    // Fetch details for each message
    const messageDetails = await Promise.all(
      data.messages.map((m: any) => getMessageById(m.id))
    );

    return {
      messages: messageDetails.filter((m): m is GmailMessage => m !== null),
      nextPageToken: data.nextPageToken,
      resultSizeEstimate: data.resultSizeEstimate || 0,
    };
  } catch (error) {
    // console.error('Error listing inbox messages:', error);
    return null;
  }
};

// List sent messages
export const listSentMessages = async (
  maxResults: number = 20,
  pageToken?: string,
  query?: string
): Promise<GmailMessageList | null> => {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  try {
    const params = new URLSearchParams({
      maxResults: String(maxResults),
      labelIds: 'SENT',
    });

    if (pageToken) params.append('pageToken', pageToken);
    if (query) params.append('q', query);

    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (!data.messages || data.messages.length === 0) {
      return { messages: [], resultSizeEstimate: 0 };
    }

    // Fetch details for each message
    const messageDetails = await Promise.all(
      data.messages.map((m: any) => getMessageById(m.id))
    );

    return {
      messages: messageDetails.filter((m): m is GmailMessage => m !== null),
      nextPageToken: data.nextPageToken,
      resultSizeEstimate: data.resultSizeEstimate || 0,
    };
  } catch (error) {
    // console.error('Error listing sent messages:', error);
    return null;
  }
};

// Get a single message by ID
export const getMessageById = async (messageId: string, format: 'full' | 'metadata' = 'metadata'): Promise<GmailMessage | null> => {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}?format=${format}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    const headers = data.payload?.headers || [];

    const fromHeader = parseHeader(headers, 'From');
    const { name: fromName, email: fromEmail } = parseEmailAddress(fromHeader);
    const dateHeader = parseHeader(headers, 'Date');

    const message: GmailMessage = {
      id: data.id,
      threadId: data.threadId,
      snippet: data.snippet || '',
      from: fromEmail,
      fromName: fromName,
      to: parseHeader(headers, 'To'),
      subject: parseHeader(headers, 'Subject') || '(no subject)',
      date: dateHeader,
      timestamp: dateHeader ? new Date(dateHeader).getTime() : 0,
      isUnread: data.labelIds?.includes('UNREAD') || false,
      isStarred: data.labelIds?.includes('STARRED') || false,
      labelIds: data.labelIds || [],
    };

    // Extract body and attachments if full format
    if (format === 'full' && data.payload) {
      const { text, html, attachments } = extractBodyAndAttachments(data.payload);
      message.body = text;
      message.bodyHtml = html;
      message.attachments = attachments;
    }

    return message;
  } catch (error) {
    // console.error('Error fetching message:', error);
    return null;
  }
};

// Get full message content
export const getMessageContent = async (messageId: string): Promise<GmailMessage | null> => {
  return getMessageById(messageId, 'full');
};

// Mark message as read
export const markAsRead = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          removeLabelIds: ['UNREAD'],
        }),
      }
    );

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`Gmail markAsRead failed (${response.status}):`, errorBody);
    }
    return response.ok;
  } catch (error) {
    console.error('Error marking message as read:', error);
    return false;
  }
};

// Mark message as unread
export const markAsUnread = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          addLabelIds: ['UNREAD'],
        }),
      }
    );

    return response.ok;
  } catch (error) {
    // console.error('Error marking message as unread:', error);
    return false;
  }
};

// Archive message (remove from inbox)
export const archiveMessage = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          removeLabelIds: ['INBOX'],
        }),
      }
    );

    return response.ok;
  } catch (error) {
    // console.error('Error archiving message:', error);
    return false;
  }
};

// Move message to trash
export const trashMessage = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/trash`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    return response.ok;
  } catch (error) {
    // console.error('Error trashing message:', error);
    return false;
  }
};

// Star a message (add STARRED label)
export const starMessage = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          addLabelIds: ['STARRED'],
        }),
      }
    );

    return response.ok;
  } catch (error) {
    return false;
  }
};

// Unstar a message (remove STARRED label)
export const unstarMessage = async (messageId: string): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          removeLabelIds: ['STARRED'],
        }),
      }
    );

    return response.ok;
  } catch (error) {
    return false;
  }
};

// Batch mark multiple messages as read
export const batchMarkAsRead = async (messageIds: string[]): Promise<{ success: number; failed: number }> => {
  const accessToken = getAccessToken();
  if (!accessToken) return { success: 0, failed: messageIds.length };

  let success = 0;
  let failed = 0;

  // Gmail doesn't have a true batch modify for individual messages via REST,
  // so we use Promise.all with concurrent requests (max ~10 at a time to avoid rate limits)
  const batchSize = 10;
  for (let i = 0; i < messageIds.length; i += batchSize) {
    const batch = messageIds.slice(i, i + batchSize);
    const results = await Promise.allSettled(
      batch.map((id) =>
        fetch(`${GMAIL_API_BASE}/users/me/messages/${id}/modify`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ removeLabelIds: ['UNREAD'] }),
        }).then((r) => r.ok)
      )
    );

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        success++;
      } else {
        failed++;
      }
    }
  }

  return { success, failed };
};

// Get attachment content and trigger download
export const getAttachment = async (
  messageId: string,
  attachmentId: string,
  filename: string,
  mimeType: string
): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(
      `${GMAIL_API_BASE}/users/me/messages/${messageId}/attachments/${attachmentId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return false;

    const data = await response.json();

    // Gmail returns base64url encoded data
    const base64 = data.data.replace(/-/g, '+').replace(/_/g, '/');

    // Convert to blob and download
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    // console.error('Error downloading attachment:', error);
    return false;
  }
};

// Check which inbox messages need a reply from the user.
// Looks at the thread: if the last message is NOT from the user, they need to reply.
// Only flags threads with more than 1 message (i.e. an ongoing conversation).
export const checkThreadsNeedingReply = async (
  messages: GmailMessage[],
  userEmail: string
): Promise<Map<string, boolean>> => {
  const accessToken = getAccessToken();
  const result = new Map<string, boolean>();

  if (!accessToken || messages.length === 0 || !userEmail) return result;

  const normalizedUserEmail = userEmail.toLowerCase();

  // Batch by 10 to avoid rate limits
  const batchSize = 10;
  for (let i = 0; i < messages.length; i += batchSize) {
    const batch = messages.slice(i, i + batchSize);
    const threadChecks = await Promise.allSettled(
      batch.map(async (msg) => {
        const response = await fetch(
          `${GMAIL_API_BASE}/users/me/threads/${msg.threadId}?format=metadata&metadataHeaders=From`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!response.ok) return { messageId: msg.id, needsReply: false };
        const data = await response.json();
        const threadMessages = data.messages || [];

        // Only flag conversations (2+ messages) where user hasn't replied last
        if (threadMessages.length < 2) {
          return { messageId: msg.id, needsReply: false };
        }

        // Get the From header of the last message in the thread
        const lastMsg = threadMessages[threadMessages.length - 1];
        const lastFromHeader = lastMsg.payload?.headers?.find(
          (h: any) => h.name.toLowerCase() === 'from'
        )?.value || '';
        const lastFromEmail = lastFromHeader.toLowerCase();

        // If the last message is NOT from the user, they need to reply
        const needsReply = !lastFromEmail.includes(normalizedUserEmail);
        return { messageId: msg.id, needsReply };
      })
    );

    for (const check of threadChecks) {
      if (check.status === 'fulfilled') {
        result.set(check.value.messageId, check.value.needsReply);
      }
    }
  }

  return result;
};

// Export default object
const gmailApi = {
  isConfigured: isGmailConfigured,
  isAuthenticated: isGmailAuthenticated,
  getAuthUrl: getGmailAuthUrl,
  handleCallback: handleGmailCallback,
  disconnect: disconnectGmail,
  getUserEmail: getGmailUserEmail,
  getUserProfile: getGmailUserProfile,
  sendEmail,
  sendBulkEmails,
  // Templates
  getTemplates: getEmailTemplates,
  saveTemplate: saveEmailTemplate,
  updateTemplate: updateEmailTemplate,
  deleteTemplate: deleteEmailTemplate,
  getTemplateById: getEmailTemplateById,
  // Drafts
  getDrafts: getEmailDrafts,
  saveDraft: saveEmailDraft,
  updateDraft: updateEmailDraft,
  deleteDraft: deleteEmailDraft,
  getDraftById: getEmailDraftById,
  // Variables
  replaceVariables: replaceTemplateVariables,
  getAvailableVariables,
  // Inbox & Sent
  listInbox: listInboxMessages,
  listSent: listSentMessages,
  getMessage: getMessageById,
  getMessageContent,
  markAsRead,
  markAsUnread,
  batchMarkAsRead,
  starMessage,
  unstarMessage,
  archiveMessage,
  trashMessage,
  getAttachment,
  checkThreadsNeedingReply,
};

export default gmailApi;
